<template>
  <v-dialog v-model="localShow" :max-width="800" :persistent="true" :fullscreen="isMobile">
    <v-card v-bind:style="{ 'min-height': !isMobile ? '550px' : '' }">
      <v-card-title class="pa-4 d-flex align-center" v-if="documentType">
        <v-icon start>mdi-send</v-icon> Send {{ documentType.name }}
        <v-spacer />
        <v-btn icon size="small" variant="text" @click="cancel">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-form ref="form" class="pl-4">
        <v-card-text>
          <v-row>
            <v-col>
              <PartyMetadata
                :focusOnLoad="focusOnParty"
                ref="party"
                :rules="requiredRule"
                :documentMetadata="partyMetadata"
                :partyTypeId="5"
                :showOrgUnit="true"
                :onPartyChanged="onPartyChanged"
                @update:value="partyMetadata.value = $event"
                @update:property="partyMetadata[$event.property] = $event.value"
              />
              <CompanyMetadata
                :documentMetadata="companyMetadata"
                v-show="companyMetadata.party"
                @update:value="companyMetadata.value = $event"
                @update:property="companyMetadata[$event.property] = $event.value"
              />
              <div v-if="partyMetadata.value && documentTypeId">
                <input type="file" ref="fileInput" style="display:none" @change="onFileSelected" />

                <!-- Empty state - upload zone -->
                <div
                  v-if="!selectedFile"
                  class="file-upload-zone mt-4"
                  :class="{ 'drag-over': isDragOver }"
                  @dragover.prevent="isDragOver = true"
                  @dragleave="isDragOver = false"
                  @drop.prevent="onFileDrop"
                  @click="$refs.fileInput.click()"
                  data-cy="drag-and-drop-zone"
                >
                  <div class="dropzone-custom-content">
                    <v-icon size="32" class="mb-2">mdi-upload</v-icon>
                    <div class="dropzone-custom-title">Select a file or drag it here</div>
                  </div>
                </div>

                <!-- File selected - card/thumbnail display -->
                <v-card v-else class="file-card mt-4" elevation="2">
                  <v-card-text class="d-flex align-center pa-3">
                    <v-icon size="40" color="primary" class="mr-3">mdi-file-document</v-icon>
                    <div class="flex-grow-1 file-info">
                      <div class="file-name">{{ selectedFile.name }}</div>
                      <div class="file-size">{{ formatFileSize(selectedFile.size) }}</div>
                    </div>
                    <v-btn icon size="small" variant="text" @click.stop="removeFile">
                      <v-icon size="18">mdi-close</v-icon>
                    </v-btn>
                  </v-card-text>
                </v-card>
              </div>
              <v-progress-linear indeterminate v-if="loading"></v-progress-linear>
            </v-col>
          </v-row>
          <v-row class="pt-4">
            <v-spacer />
            <v-btn class="mr-3" v-show="!disableNext" variant="flat" color="primary" @click="next()" data-cy="next">
              Next
              <v-icon end>mdi-arrow-right</v-icon>
            </v-btn>
          </v-row>
        </v-card-text>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import batchService from "@/services/batchService";
import documentTypeService from "@/services/documentTypeService";
import { mapState, mapGetters } from "vuex";
import httpService from "@/services/httpService";
import PartyMetadata from "@/components/metadata/PartyMetadata.vue";
import Auth0Mixin from "../views/Auth0Mixin";
import responsiveService from "@/services/responsiveService";
import CompanyMetadata from "../components/metadata/CompanyMetadata.vue";
import partyService from "../services/partyService";

const requiredRule = [v => !!v || "Required"];

export default {
  components: { CompanyMetadata, PartyMetadata },
  mixins: [Auth0Mixin],

  props: {
    show: Boolean,
    documentTypeId: Number,
    cancel: Function,
  },

  emits: ["update:show"],
  data() {
    return {
      localShow: this.show,
      loading: false,
      focusOnParty: true,
      requiredRule,
      selectedFile: null,
      isDragOver: false,
      fileUploadData: {
        batchName: null,
        userId: null,
        folderId: 1,
        numberOfDocuments: 1,
        returnBeforeOcr: false,
      },
      documentType: undefined,
      partyMetadata: {
        value: undefined,
        metadata: {
          name: "Send To",
          key: "sendTo",
        },
      },
      companyMetadata: {
        value: undefined,
        displayValue: undefined,
        metadata: {
          name: "Bill To",
          key: "billTo",
        },
        party: null,
      },
    };
  },

  computed: {
    ...mapGetters("auth0Store", ["user"]),

    ...mapState("documentTypeStore", ["documentTypes"]),

    disableNext() {
      return this.loading || !this.partyMetadata.value;
    },

    validForm() {
      return !!this.selectedFile;
    },

    isMobile() {
      return responsiveService.isMobile(this.$vuetify.display);
    },
  },

  watch: {
    show(val) {
      this.localShow = val;
      this.focusOnParty = val;
      if (!val) this.removeFile();
    },
    localShow(val) {
      if (!val) this.$emit("update:show", val);
    },

    documentTypeId() {
      this.init();
    },
  },

  methods: {
    async init() {
      this.documentType = await documentTypeService.getFromTemplate(this.documentTypeId);
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },

    onFileSelected(event) {
      const files = event.target.files;
      if (files.length > 0) {
        this.selectedFile = files[0];
      }
    },

    onFileDrop(event) {
      this.isDragOver = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.selectedFile = files[0];
      }
    },

    removeFile() {
      this.selectedFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    async next() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      if (!this.selectedFile) {
        this.$root.$snackbar.error("Please select a file to send");
        return;
      }

      this.fileUploadData.batchName = batchService.generateBatchName();
      this.fileUploadData.receivingPartyId = this.partyMetadata.value;
      this.fileUploadData.receivingCompanyId = this.companyMetadata.value;
      this.fileUploadData.ReceivingCompanyName = this.companyMetadata.displayValue;
      this.fileUploadData.documentTypeId = this.documentType.id;
      this.fileUploadData.userId = this.user.id;

      this.loading = true;

      try {
        await httpService.postFile("/documents/upload", this.selectedFile, { fileUploadData: this.fileUploadData });

        let docIds = await batchService.getBatchDocumentIds(this.fileUploadData.batchName, this.fileUploadData.userId);

        if (docIds.length === 1) {
          if (this.fileUploadData.returnBeforeOcr) {
            this.loading = false;
            this.cancel();
            this.removeFile();
          } else {
            this.$router.push({
              path: `/documents/${docIds[0]}`,
            });
          }
        } else {
          this.$router.push({ path: `/documents/batch/${encodeURIComponent(this.fileUploadData.batchName)}` });
        }
      } catch (error) {
        this.loading = false;
        this.$root.$snackbar.error("Failed to upload file");
      }
    },

    async onPartyChanged() {
      if (this.partyMetadata?.value)
        this.companyMetadata.party = await partyService.getByIdWithConnectedOrgUnits(this.partyMetadata?.value);
    },
  },
};
</script>

<style scoped>
.file-upload-zone {
  border: 2px dashed rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: rgb(var(--v-theme-surface-light));
}

.file-upload-zone:hover {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgb(var(--v-theme-surface-light));
}

.file-upload-zone.drag-over {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgb(var(--v-theme-surface-light));
}

.dropzone-custom-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.dropzone-custom-title {
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 14px;
}

.file-card {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  transition: box-shadow 0.2s;
}

.file-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

.file-info {
  overflow: hidden;
}

.file-name {
  font-weight: 500;
  font-size: 14px;
  color: rgb(var(--v-theme-on-surface));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin-top: 2px;
}
</style>
